import I18n from 'I18n';
import { Range } from 'immutable';
import { PureComponent } from 'react';
import CALENDAR_VIEW_TYPES from '../../constants/CalendarViewTypes';
import { getCachedMoment, getDaysInMonth, getDisplayMonthRows, getFirstOffset, getMomentMonth, getWeekdaysRange, isSame } from '../../lib/time';
import { CompactEventIndicator, CompactRow, CompactRowWrapper, Event, EventsWrapper, Row, RowWrapper, Week } from './MonthCalendarStyles';
import MonthDay from './MonthDay';
import { getEventWeekDetails } from '../../lib/eventWeek';
import { doesEventStartBeforeDay } from '../../lib/event';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getDateFromWeekAndDayNumber = ({
  week,
  dayOfWeek,
  displayYear,
  displayMonth
}) => {
  const firstOffset = getFirstOffset(displayYear, displayMonth);
  const zeroToOneIndexOffset = 1;
  const result = dayOfWeek + zeroToOneIndexOffset + week * 7 - firstOffset;
  return result;
};
export default class MonthCalendarGrid extends PureComponent {
  getDisplayDate(date) {
    const {
      displayMonth,
      displayYear,
      timezone
    } = this.props;
    return getMomentMonth(displayYear, displayMonth, date, timezone);
  }
  renderEvent({
    event,
    index,
    week,
    day,
    dayIndex
  }) {
    const {
      start,
      end
    } = event.props;
    const {
      hideWeekends = false,
      displayYear,
      displayMonth,
      timezone
    } = this.props;
    const daysInMonth = getDaysInMonth(displayYear, displayMonth);
    const currentDate = getDateFromWeekAndDayNumber({
      week,
      dayOfWeek: day,
      displayYear,
      displayMonth
    });
    const thisDay = getCachedMoment(undefined, timezone).year(displayYear).month(displayMonth).date(currentDate > daysInMonth ? daysInMonth : currentDate).startOf('day');
    if (doesEventStartBeforeDay({
      start,
      time: thisDay,
      timezone
    }) && dayIndex !== 0) {
      return null;
    }
    const {
      lengthForWeek,
      startPosition
    } = getEventWeekDetails({
      displayDay: thisDay.dayOfYear(),
      displayYear,
      dayOfWeek: day,
      start,
      end,
      timezone,
      hideWeekends
    });
    return /*#__PURE__*/_jsx(Event, {
      "data-test-id": `event--${day}.${index}`,
      dayOfWeek: startPosition,
      dayLength: lengthForWeek,
      index: index,
      children: event
    }, `${day}.${index}`);
  }
  renderDayEvents({
    week,
    day,
    dayIndex
  }) {
    const {
      events,
      compact,
      renderOverflow,
      displayYear,
      displayMonth
    } = this.props;
    const {
      displayEvents,
      overflowCount
    } = events.get(week).get(day);
    if (compact) {
      if (displayEvents.size > 0 || overflowCount > 0) {
        return /*#__PURE__*/_jsx(CompactEventIndicator, {
          "data-test-id": `month-day-events-compact-indicator--${week}-${day}`,
          dayOfWeek: dayIndex
        }, `${week}.${dayIndex}`);
      }
      return null;
    }
    return /*#__PURE__*/_jsxs("span", {
      "data-test-id": `month-day-events--${week}-${day}`,
      children: [displayEvents.map(({
        event,
        displayIndex
      }) => {
        return this.renderEvent({
          event,
          index: displayIndex,
          week,
          day,
          dayIndex
        });
      }), renderOverflow({
        actualDate: this.getDisplayDate(getDateFromWeekAndDayNumber({
          week,
          dayOfWeek: day,
          displayYear,
          displayMonth
        })),
        week,
        dayOfWeek: day,
        dayIndex
      })]
    }, `${week}.${day}`);
  }
  renderDay(date, daysInMonth) {
    const {
      currentTime,
      displayYear,
      displayMonth,
      compact,
      timezone
    } = this.props;
    const actualDate = this.getDisplayDate(date);
    const isCurrentDay = isSame(currentTime, actualDate, timezone, 'day');
    const isToday = isSame(I18n.moment.portalTz().startOf('day'), actualDate, timezone, 'day');
    const isOutsideMonth = date <= 0 || date > daysInMonth;
    const thisMoment = (timezone ? I18n.moment().tz(timezone) : I18n.moment.portalTz()).year(displayYear).month(displayMonth).date(date);
    return /*#__PURE__*/_jsx(MonthDay, {
      actualDate: actualDate,
      compact: compact,
      isOutsideMonth: isOutsideMonth,
      isCurrentDay: isCurrentDay,
      isToday: isToday,
      droppedMoment: actualDate.startOf('day'),
      thisMoment: thisMoment,
      viewType: CALENDAR_VIEW_TYPES.MONTH
    }, actualDate.date());
  }
  renderWeek(week) {
    const {
      displayYear,
      displayMonth,
      hideWeekends
    } = this.props;
    const daysInMonth = getDaysInMonth(displayYear, displayMonth);
    return getWeekdaysRange(hideWeekends, I18n.moment.locale()).map(dayOfWeek => {
      return this.renderDay(getDateFromWeekAndDayNumber({
        week,
        dayOfWeek,
        displayYear,
        displayMonth
      }), daysInMonth);
    });
  }
  renderRow(week) {
    const {
      displayYear,
      displayMonth,
      compact,
      hideWeekends
    } = this.props;
    const displayMonthRows = getDisplayMonthRows(displayYear, displayMonth);
    const RowWrapperComponent = compact ? CompactRowWrapper : RowWrapper;
    const RowComponent = compact ? CompactRow : Row;
    return /*#__PURE__*/_jsx(RowWrapperComponent, {
      children: /*#__PURE__*/_jsxs(RowComponent, {
        isLastRow: week === displayMonthRows - 1,
        children: [/*#__PURE__*/_jsx(EventsWrapper, {
          children: getWeekdaysRange(hideWeekends, I18n.moment.locale()).map((day, dayIndex) => {
            return this.renderDayEvents({
              week,
              day,
              dayIndex
            });
          })
        }), /*#__PURE__*/_jsx(Week, {
          children: this.renderWeek(week)
        })]
      })
    }, week);
  }
  render() {
    const {
      displayYear,
      displayMonth
    } = this.props;
    const displayMonthRows = getDisplayMonthRows(displayYear, displayMonth);
    return Range(0, displayMonthRows).map(weekIndex => {
      return this.renderRow(weekIndex);
    }).toArray();
  }
}